import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '../firebase.js';
import { USER_CLAIMS_COLLECTION } from '@kilat/shared';

/**
 * `userClaims/{uid}` — the revocation watermark for the SIGNED-IN USER ONLY.
 *
 * Read-only by design, and self-read only at the rules layer. The client
 * listens to its own document so that when the server re-mints its claims —
 * a role change, a removal — the app can force a token refresh within seconds
 * instead of leaving the operator staring at permission-denied errors until
 * Firebase happens to refresh on its own.
 *
 * There is no setter, and there never should be: a client that could write
 * this could un-revoke itself in one line. `infra/firestore.rules` denies the
 * write outright; the server uses the Admin SDK, which bypasses rules.
 *
 * The document carries no secret — one epoch-ms number about the reader's own
 * account — which is why the read rule is a plain uid match rather than a role
 * helper. Routing it through a role helper would make this listener pay an
 * extra document read on every delivery.
 */
export function watchOwnClaimsWatermark(
  uid: string,
  onRevokedAt: (revokedAt: number | null) => void,
): () => void {
  return onSnapshot(
    doc(db, `${USER_CLAIMS_COLLECTION}/${uid}`),
    (snap) => {
      if (!snap.exists()) {
        onRevokedAt(null);
        return;
      }
      const value = snap.data()['revokedAt'];
      onRevokedAt(typeof value === 'number' ? value : null);
    },
    () => {
      // A read failure here is not worth surfacing: the watermark is a UX
      // accelerator, and the security property lives in the rules regardless.
      onRevokedAt(null);
    },
  );
}
